/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable no-lonely-if */
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { TLivePrice } from '../Pages/INStockDetailPage/types'
import { useGetFnOOIFromSocket } from './useGetINStockLivePriceFromSocket'
import {
  formatNumberToCommaSeparatedString,
  formatNumberToINRCommaSeparatedString,
  formatNumberToUSDCommaSeparatedString,
} from '@/root/utils/common/numbers/format-number-to-string'
import { useLivePrice } from '@ind-money/socket-manager'

const currencyFormatter = (price: number, addSymbol = true) => {
  if (typeof price !== 'number') return ''
  if (addSymbol) {
    return formatNumberToINRCommaSeparatedString(price)
  }
  return formatNumberToCommaSeparatedString(price, 2)
}

export function PercentChangeFormatter({
  percent_change,
  percentArrowSuffix,
  absolute_change,
  showAbsoluteChange = true,
}: {
  percent_change: number
  percentArrowSuffix?: boolean
  absolute_change?: number
  showAbsoluteChange?: boolean
}) {
  const changeIsPositive = percent_change >= 0

  let changeUpOrDownArrow: JSX.Element | null = null

  if (changeIsPositive) {
    changeUpOrDownArrow = <span>&#9650;</span>
  } else {
    changeUpOrDownArrow = (
      <span className="relative inline-block rotate-180 transform">
        &#9650;
      </span>
    )
  }
  return (
    <span
      className={classNames({
        'text-brand-green-dark': changeIsPositive,
        'text-brand-red-cinnabar': !changeIsPositive,
      })}
    >
      {typeof absolute_change === 'number' && showAbsoluteChange && (
        <span>{`${absolute_change.toFixed(2)} (`}</span>
      )}
      {!percentArrowSuffix && changeUpOrDownArrow}
      {percent_change && typeof percent_change === 'number'
        ? Math.abs(percent_change).toFixed(2)
        : percent_change}
      %{percentArrowSuffix && changeUpOrDownArrow}
      {typeof absolute_change === 'number' && showAbsoluteChange && ')'}
    </span>
  )
}

export function LiveOICommon({
  price,
  percent_change,
  percent_change_color,
  socket_path,
  layout = 'horizontal',
  priceClassNames = 'text-xl font-medium',
  percentClassNames = 'font-normal',
  showPercentChange = true,
}: {
  price?: string | number
  percent_change?: string | number
  percent_change_color?: string
  socket_path?: string
  layout?: 'horizontal' | 'vertical'
  priceClassNames?: string
  percentClassNames?: string
  showPercentChange?: boolean
}) {
  const [livePrice, setLivePrice] = useState<TLivePrice | null>(null)

  const { asPath } = useRouter()
  useGetFnOOIFromSocket(socket_path, setLivePrice)

  useEffect(() => {
    setLivePrice(null)
  }, [asPath])

  if (livePrice) {
    if (layout === 'vertical') {
      if (typeof livePrice.price === 'string') {
        // if price and percent change are already formatted
        return (
          <div>
            <p className={priceClassNames}>{livePrice.price}</p>

            {typeof livePrice.percent_change === 'string' &&
              showPercentChange && (
                <p
                  className={percentClassNames}
                  style={{
                    color: livePrice.color ?? '',
                  }}
                >
                  {livePrice.percent_change}
                </p>
              )}
          </div>
        )
      } else if (typeof livePrice.price === 'number') {
        // price and percent change are numbers
        return (
          <div>
            <p className={priceClassNames}>
              {formatNumberToCommaSeparatedString(livePrice.price)}
            </p>
            {typeof livePrice.percent_change === 'number' &&
              showPercentChange && (
                <p className={percentClassNames}>
                  <PercentChangeFormatter
                    percent_change={livePrice.percent_change}
                  />
                </p>
              )}
          </div>
        )
      }
    } else {
      if (typeof livePrice.price === 'string') {
        // if price and percent change are already formatted
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>{livePrice.price}</p>

            {typeof livePrice.percent_change === 'string' &&
              showPercentChange && (
                <p
                  className={percentClassNames}
                  style={{
                    color: livePrice.color ?? '',
                  }}
                >
                  {livePrice.percent_change}
                </p>
              )}
          </div>
        )
      } else if (typeof livePrice.price === 'number') {
        // price and percent change are numbers
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>
              {formatNumberToCommaSeparatedString(livePrice.price)}
            </p>
            {typeof livePrice.percent_change === 'number' &&
              showPercentChange && (
                <p className={percentClassNames}>
                  <PercentChangeFormatter
                    percent_change={livePrice.percent_change}
                  />
                </p>
              )}
          </div>
        )
      }
    }
  } else {
    // Socket disconnected OR no socket path provided
    if (layout === 'vertical') {
      if (typeof price === 'string') {
        // if price and percent change are already formatted
        return (
          <div>
            <p className={priceClassNames}>{price}</p>

            {typeof percent_change === 'string' && showPercentChange && (
              <p
                className={percentClassNames}
                style={{
                  color: percent_change_color,
                }}
              >
                {percent_change}
              </p>
            )}
          </div>
        )
      } else if (typeof price === 'number') {
        // price and percent change are numbers
        return (
          <div>
            <p className={priceClassNames}>
              {formatNumberToCommaSeparatedString(price)}
            </p>
            {typeof percent_change === 'number' && showPercentChange && (
              <p className={percentClassNames}>
                <PercentChangeFormatter percent_change={percent_change} />
              </p>
            )}
          </div>
        )
      }
    } else {
      // horizontal layout

      if (typeof price === 'string') {
        // if price and percent change are already formatted
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>{price}</p>

            {typeof percent_change === 'string' && showPercentChange && (
              <p
                className={percentClassNames}
                style={{
                  color: percent_change_color,
                }}
              >
                {percent_change}
              </p>
            )}
          </div>
        )
      } else if (typeof price === 'number') {
        // price and percent change are numbers
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>
              {formatNumberToCommaSeparatedString(price)}
            </p>
            {typeof percent_change === 'number' && showPercentChange && (
              <p
                className={percentClassNames}
                style={{
                  color: percent_change_color,
                }}
              >
                {percent_change}%
              </p>
            )}
          </div>
        )
      }
    }
  }

  return null
}

export default function LivePriceCommon({
  price,
  percent_change,
  percent_change_color,
  socket_path,
  web_socket_path,
  us_web_socket_path,
  layout = 'horizontal',
  priceClassNames = 'text-xl font-medium',
  percentClassNames = 'font-normal',
  percentArrowSuffix = false,
  absolute_change,
  showPercentChange = true,
  showAbsoluteChange = true,
  is_global = false,
  showCurrencySymbol = true,
  priceWrapperClassNames = '',
}: {
  price?: string | number
  percent_change?: string | number
  percent_change_color?: string
  socket_path?: string
  web_socket_path?: string
  /** if provided, will use US prices websocket instead of Indian prices websocket */
  us_web_socket_path?: string
  layout?: 'horizontal' | 'vertical'
  priceClassNames?: string
  percentClassNames?: string
  percentArrowSuffix?: boolean
  absolute_change?: number
  showPercentChange?: boolean
  showAbsoluteChange?: boolean
  is_global?: boolean
  showCurrencySymbol?: boolean
  priceWrapperClassNames?: string
}) {
  const livePrice = useLivePrice({
    enabled: !!web_socket_path || !!socket_path || !!us_web_socket_path,
    ...(us_web_socket_path
      ? {
          usWebSocketPath: us_web_socket_path,
        }
      : {
          webSocketPath: web_socket_path ?? '',
          natsSocketPath: socket_path ?? '',
          source: web_socket_path ? 'websocket' : 'nats',
        }),
  })

  if (livePrice.price) {
    if (layout === 'vertical') {
      if (typeof livePrice.price === 'string') {
        // if price and percent change are already formatted
        return (
          <div>
            <p className={priceClassNames}>{livePrice.price}</p>

            {typeof livePrice.percentChange === 'string' &&
              showPercentChange && (
                <p
                  className={percentClassNames}
                  style={{
                    color: livePrice.color ?? '',
                  }}
                >
                  {livePrice.percentChange}
                </p>
              )}
          </div>
        )
      } else if (typeof livePrice.price === 'number') {
        // price and percent change are numbers
        return (
          <div>
            <p className={priceClassNames}>
              {is_global
                ? formatNumberToUSDCommaSeparatedString(livePrice.price)
                : formatNumberToINRCommaSeparatedString(livePrice.price)}
            </p>
            {typeof livePrice.percentChange === 'number' &&
              showPercentChange && (
                <p className={percentClassNames}>
                  <PercentChangeFormatter
                    percent_change={livePrice.percentChange}
                    percentArrowSuffix={percentArrowSuffix}
                    absolute_change={
                      livePrice.absoluteChange ?? absolute_change
                    }
                    showAbsoluteChange={showAbsoluteChange}
                  />
                </p>
              )}
          </div>
        )
      }
    } else {
      if (typeof livePrice.price === 'string') {
        // if price and percent change are already formatted
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>{livePrice.price}</p>

            {typeof livePrice.percentChange === 'string' &&
              showPercentChange && (
                <p
                  className={percentClassNames}
                  style={{
                    color: livePrice.color ?? '',
                  }}
                >
                  {livePrice.percentChange}
                </p>
              )}
          </div>
        )
      } else if (typeof livePrice.price === 'number') {
        // price and percent change are numbers
        return (
          <div
            className={classNames(
              'flex items-center gap-2',
              priceWrapperClassNames,
            )}
          >
            <p className={priceClassNames}>
              {is_global
                ? formatNumberToUSDCommaSeparatedString(livePrice.price)
                : currencyFormatter(livePrice.price, showCurrencySymbol)}
            </p>
            {typeof livePrice.percentChange === 'number' &&
              showPercentChange && (
                <p className={percentClassNames}>
                  {' '}
                  <PercentChangeFormatter
                    percent_change={livePrice.percentChange}
                    percentArrowSuffix={percentArrowSuffix}
                    absolute_change={
                      livePrice.absoluteChange ?? absolute_change
                    }
                    showAbsoluteChange={showAbsoluteChange}
                  />
                </p>
              )}
          </div>
        )
      }
    }
  } else {
    // Socket disconnected OR no socket path provided
    if (layout === 'vertical') {
      if (typeof price === 'string') {
        // if price and percent change are already formatted
        return (
          <div>
            <p className={priceClassNames}>{price}</p>

            {typeof percent_change === 'string' && showPercentChange && (
              <p
                className={percentClassNames}
                style={{
                  color: percent_change_color,
                }}
              >
                {percent_change}
              </p>
            )}
          </div>
        )
      } else if (typeof price === 'number') {
        // price and percent change are numbers
        return (
          <div>
            <p className={priceClassNames}>
              {is_global
                ? formatNumberToUSDCommaSeparatedString(price, true)
                : formatNumberToINRCommaSeparatedString(price)}
            </p>
            {typeof percent_change === 'number' && showPercentChange && (
              <p className={percentClassNames}>
                {' '}
                <PercentChangeFormatter
                  percent_change={percent_change}
                  percentArrowSuffix={percentArrowSuffix}
                  absolute_change={absolute_change}
                  showAbsoluteChange={showAbsoluteChange}
                />
              </p>
            )}
          </div>
        )
      }
    } else {
      // horizontal layout

      if (typeof price === 'string') {
        // if price and percent change are already formatted
        return (
          <div className="flex items-center gap-2">
            <p className={priceClassNames}>{price}</p>

            {typeof percent_change === 'string' && showPercentChange && (
              <p
                className={percentClassNames}
                style={{
                  color: percent_change_color,
                }}
              >
                {percent_change}
              </p>
            )}
          </div>
        )
      } else if (typeof price === 'number') {
        // price and percent change are numbers
        return (
          <div
            className={classNames(
              'flex items-center gap-2',
              priceWrapperClassNames,
            )}
          >
            <p className={priceClassNames}>
              {is_global
                ? formatNumberToUSDCommaSeparatedString(price, true)
                : currencyFormatter(price, showCurrencySymbol)}
            </p>
            {typeof percent_change === 'number' && showPercentChange && (
              <p className={percentClassNames}>
                {' '}
                <PercentChangeFormatter
                  percent_change={percent_change}
                  percentArrowSuffix={percentArrowSuffix}
                  absolute_change={absolute_change}
                  showAbsoluteChange={showAbsoluteChange}
                />
              </p>
            )}
          </div>
        )
      }
    }
  }

  return null
}
